import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

const UnsavedChangesContext = createContext(null);

export function UnsavedChangesProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("admin-account");

  const [isDirty, setIsDirty] = useState(false);
  const [showGuard, setShowGuard] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const discardHandlerRef = useRef(null);

  const setUnsavedChanges = useCallback((dirty, onDiscard = null) => {
    setIsDirty(Boolean(dirty));
    discardHandlerRef.current =
      dirty && typeof onDiscard === "function" ? onDiscard : null;
  }, []);

  const clearUnsavedChanges = useCallback(() => {
    setIsDirty(false);
    setShowGuard(false);
    setPendingNavigation(null);
    discardHandlerRef.current = null;
  }, []);

  const requestNavigation = useCallback(
    (to, options = {}) => {
      const currentPath = `${location.pathname}${location.search}${location.hash}`;

      if (typeof to === "string" && to === currentPath) return;

      if (!isDirty) {
        navigate(to, options);
        return;
      }

      setPendingNavigation({ to, options });
      setShowGuard(true);
    },
    [isDirty, location.hash, location.pathname, location.search, navigate],
  );

  const keepEditing = useCallback(() => {
    setShowGuard(false);
    setPendingNavigation(null);
  }, []);

  const discardAndLeave = useCallback(() => {
    const pending = pendingNavigation;

    if (discardHandlerRef.current) {
      discardHandlerRef.current();
    }

    setIsDirty(false);
    setShowGuard(false);
    setPendingNavigation(null);
    discardHandlerRef.current = null;

    if (pending) {
      navigate(pending.to, pending.options);
    }
  }, [navigate, pendingNavigation]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!showGuard) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        keepEditing();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showGuard, keepEditing]);

  const value = useMemo(
    () => ({
      isDirty,
      setUnsavedChanges,
      clearUnsavedChanges,
      requestNavigation,
    }),
    [isDirty, setUnsavedChanges, clearUnsavedChanges, requestNavigation],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}

      {showGuard &&
        createPortal(
          <div
            className="admin-account-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                keepEditing();
              }
            }}
          >
            <div
              className="admin-account-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="admin-unsaved-changes-title"
            >
              <div className="admin-account-modal__icon">
                <AlertTriangle size={25} />
              </div>

              <h2 id="admin-unsaved-changes-title">{t("leave.title")}</h2>

              <p>{t("leave.message")}</p>

              <div className="admin-account-modal__actions">
                <button
                  type="button"
                  className="admin-account-button admin-account-button--secondary"
                  onClick={keepEditing}
                >
                  {t("leave.keepEditing")}
                </button>

                <button
                  type="button"
                  className="admin-account-button admin-account-button--danger"
                  onClick={discardAndLeave}
                >
                  {t("leave.discard")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);

  if (!context) {
    throw new Error(
      "useUnsavedChanges must be used inside UnsavedChangesProvider",
    );
  }

  return context;
}
